import { createSelector, createSlice } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import { permissionsApi } from "permissions/_api/permissions-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery from "permissions/_api/queries/permission-query";
import Permission from "permissions/permission";
import { PermissionJSON } from "permissions/_schemas/permission-schema";
import { SessionPermissions } from "permissions/session/session-permissions";
import Session from "users/__session/session";

type PermissionStoreState = {
  session: {
    entries: PermissionJSON[] | undefined;
    loading: boolean;
    error: string | null;
  };
};

const initialState: PermissionStoreState = {
  session: {
    entries: undefined,
    loading: false,
    error: null,
  },
};

function getRejectedApiErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    if ("data" in error) {
      const data = (error as { data?: unknown }).data;
      return typeof data === "string" ? data : JSON.stringify(data ?? error);
    }
    if ("error" in error && typeof (error as { error?: unknown }).error === "string") {
      return (error as { error: string }).error;
    }
    if ("message" in error && typeof (error as { message?: unknown }).message === "string") {
      return (error as { message: string }).message;
    }
  }
  return JSON.stringify(error) || "An error occurred.";
}

/**
 * Compatibility bridge for screens outside the permissions workspace that still
 * derive authorization from the session permissions selector. Permission list
 * and template data are owned directly by RTK Query instead.
 */
export const populateSessionPermissions = ({ session }: { session: Session }) => {
  if (!session?.authToken || !session.user?.id) {
    return permissionsApi.endpoints.getSessionPermissions.initiate({ query: undefined });
  }

  const query = new PermissionQuery({
    filterParameters: new PermissionFilterParameters({
      granteeUserIds: [session.user.id],
      hasParent: false,
    }),
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
  });

  return permissionsApi.endpoints.getSessionPermissions.initiate({ query });
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(permissionsApi.endpoints.getSessionPermissions.matchPending, (state) => {
      state.session.loading = true;
      state.session.error = null;
    });
    builder.addMatcher(permissionsApi.endpoints.getSessionPermissions.matchFulfilled, (state, action) => {
      if (!action.payload) {
        // A fulfilled request answered the question, even when it answered "none". Leaving `entries`
        // undefined here made that indistinguishable from "not loaded yet", and the two are read very
        // differently: `selectSessionPermissions` short-circuits on undefined, so every consumer's
        // bootstrap effect — `if (!sessionPermissions && !error && !loading) dispatch(...)` — saw a
        // request that had settled with nothing to show and no reason to retry. RTK Query then served
        // the same cached result without a new request, so the dependencies stopped changing and the
        // page sat on its spinner permanently, with no error anywhere to explain it.
        //
        // The cost of that was not a slow page: it was the wrong page. Screens gated on this selector
        // read `!sessionPermissions` as "still loading" and render a loader ABOVE their own
        // "you do not have access" branch, so the person who should have been told they lack access
        // waited instead. An empty list is the honest answer and lets that branch render.
        state.session.entries = [];
        state.session.loading = false;
        state.session.error = null;
        return;
      }

      const response = PaginatedResponse.from(action.payload.data, action.payload.headers);
      if (response.hasNextPage) {
        console.error("populateSessionUserPermissions: more pages exist, but only first page was loaded.");
      }

      state.session.entries = response.data;
      state.session.loading = false;
      state.session.error = null;
    });
    builder.addMatcher(permissionsApi.endpoints.getSessionPermissions.matchRejected, (state, action) => {
      state.session.loading = false;
      state.session.error = getRejectedApiErrorMessage(action.payload ?? action.error);
    });
  },
});

const selectSessionPermissions = createSelector(
  [(state: RootState) => state.permissions?.session.entries],
  (entries) => {
    const permissionData = entries?.map((json) => Permission.fromJSON(json));
    return permissionData && new SessionPermissions(permissionData);
  },
);

export const getSessionPermissions = (state: RootState) => selectSessionPermissions(state);
export const getIsLoadingSessionPermissions = (state: RootState) => state.permissions.session.loading;
export const getErrorLoadingSessionPermissions = (state: RootState) => state.permissions.session.error;

export default permissionsSlice;
